/**
 * Minimal media path utilities.
 * Two core functions: mediaPath() for directory prefix, cdnUrl() for full CDN URL.
 * No short key extraction, no storage key reconstruction — just filename in, URL out.
 */

import MEDIA_PATH_TEMPLATES from "./mediaPathTemplates.json";

export const CDN_IMAGE_BASE = "https://media.ziniz.online";

export const MANIFEST_FILE = "manifest.json";

export const ITEMS_SHARED_GALLERY_ITEM_ID = "GALLERY";

export function getDeployEnv() {
  return (process.env.REACT_APP_DEPLOY_ENV || "Test").trim() || "Test";
}

export function cdnSafeMediaPathSegment(id) {
  if (id == null || id === "") return "";
  const s = String(id).replace(/^HubId:/i, "");
  return s.includes("#") ? s.split("#")[1] || s : s;
}

/**
 * Core path builder — returns the directory prefix for an entity.
 * @param {{ entity: "hub"|"biz"|"item", fileType: "media"|"docs", hubId?: string, bizId?: string }} opts
 * @returns {string} e.g. "MyHub/Test/public/hub/abc/biz/xyz/items/media"
 */
export function mediaPath({ entity, fileType, hubId, bizId } = {}) {
  const template = MEDIA_PATH_TEMPLATES.templates.find(
    (t) => t.entity === entity && t.type === fileType
  );
  if (!template) throw new Error(`Unknown media path: ${entity}:${fileType}`);
  const env = getDeployEnv();
  let path = template.path.replace("{env}", env);
  if (hubId != null) path = path.replace("{hubId}", cdnSafeMediaPathSegment(hubId));
  if (bizId != null) path = path.replace("{bizId}", cdnSafeMediaPathSegment(bizId));
  return path;
}

/**
 * Core CDN URL builder — returns full CDN URL from entity + filename.
 * @param {{ entity: "hub"|"biz"|"item", fileType: "media"|"docs", hubId?: string, bizId?: string, fileName: string }} opts
 * @returns {string} e.g. "https://media.ziniz.online/MyHub/Test/public/hub/abc/biz/xyz/items/media/photo.jpg"
 */
export function cdnUrl({ entity, fileType, hubId, bizId, fileName } = {}) {
  if (!fileName) return null;
  const prefix = mediaPath({ entity, fileType, hubId, bizId });
  const encoded = encodeURIComponent(String(fileName).replace(/^\//, ""));
  return `${CDN_IMAGE_BASE}/${prefix}/${encoded}`;
}

/**
 * Low-level utility: any storage key → CDN URL.
 * Kept for generic use (e.g. BusinessDocsPanel manifest fetch).
 */
export function cdnUrlForStorageKey(storageKey) {
  if (!storageKey) return null;
  const key = String(storageKey).replace(/^\//, "");
  const parts = key.split("/").map((p) => encodeURIComponent(p));
  return `${CDN_IMAGE_BASE}/${parts.join("/")}`;
}

export function uploadedOrderFileUrl({ orderId, hubId, bizId, fileName } = {}) {
  if (!orderId || !hubId || !bizId || !fileName) return null;
  const storageId = (value, prefixPattern) => String(value || '')
    .replace(prefixPattern, '')
    .replace(/#/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '_');
  const key = [
    'MyHub',
    getDeployEnv(),
    'public',
    'order',
    storageId(orderId, /^ORDER#/i),
    'hub',
    storageId(hubId, /^HUB#/i),
    'biz',
    storageId(bizId, /^BIZ#/i),
    String(fileName),
  ].join('/');
  return cdnUrlForStorageKey(key);
}

/**
 * Parse a folder prefix from mediaPath output back into hubId/bizId/segment.
 * Updated regexes for v2 paths: public/hub/{hubId}/...
 */
export function parseMediaFolderPrefix(pathPrefix) {
  if (!pathPrefix || typeof pathPrefix !== "string") return null;
  const normalized = pathPrefix.replace(/\/$/, "");
  const itemsRe =
    /^MyHub\/[^/]+\/public\/hub\/([^/]+)\/biz\/([^/]+)\/items\/media$/;
  const mItems = normalized.match(itemsRe);
  if (mItems) {
    return { hubId: mItems[1], bizId: mItems[2], gallerySegment: "items" };
  }
  const bizRe = /^MyHub\/[^/]+\/public\/hub\/([^/]+)\/biz\/([^/]+)\/media$/;
  const mBiz = normalized.match(bizRe);
  if (mBiz) {
    return { hubId: mBiz[1], bizId: mBiz[2], gallerySegment: "biz" };
  }
  const hubRe = /^MyHub\/[^/]+\/public\/hub\/([^/]+)\/media$/;
  const mHub = normalized.match(hubRe);
  if (mHub) {
    return { hubId: mHub[1], bizId: null, gallerySegment: "hub" };
  }
  return null;
}

/** Basename from a full storage key (still needed during transition). */
export function basenameFromStorageKey(fullKey) {
  if (!fullKey || typeof fullKey !== "string") return "";
  const s = fullKey.replace(/\\/g, "/");
  const i = s.lastIndexOf("/");
  return i >= 0 ? s.slice(i + 1) : s;
}

export function defaultItemGalleryTagsFromStoreType(storeType) {
  const raw = storeType == null ? "" : String(storeType);
  const parts = raw.split(/[,\s]+/).map((x) => x.trim().toUpperCase()).filter(Boolean);
  const tags = [];
  if (parts.some((p) => p === "P" || p === "PRODUCT")) tags.push("product");
  if (parts.some((p) => p === "S" || p === "SERVICE")) tags.push("service");
  return tags;
}
